import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../../context/searchContext";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../../hooks/useListFilters";
import useI18n from "../../../../../../hooks/useTranslations";
import { Shuttle } from "../../../../../../http/patrol/types/shuttles";
import { useShuttlesApi } from "../../../../../../http/patrol/useShuttles";
import { PaginationQuery } from "../../../../../../types";
import { formatBoolean } from "../../../../../../utils";
import FlexContent from "../../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../../ui/ListItemMenu";
import ModalDialog from "../../../../../ui/ModalDialog";
import SearchBar from "../../../../../ui/SearchBar";
import Table from "../../../../../ui/Table";
import Visibility from "../../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import CreateShuttleModalDialogButton from "./CreateShuttleModalDialogButton";
import ShuttleForm from "./ShuttleForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const Shuttles: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { patrolSystemTypeId } = useParams<{ patrolSystemTypeId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "patrolShuttles" });
  const aborter = useRef(new AbortController());
  const shuttlesApi = useShuttlesApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await shuttlesApi.updateAdminShuttleVisibility(id, { visibility }, { systemTypeId: patrolSystemTypeId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToEdit, setItemToEdit] = useState<Shuttle>();
  const [itemToDelete, setItemToDelete] = useState<Shuttle>();
  const { accessesAction, accessesDialog } = useEntityAccesses<Shuttle>("patrol.shuttle");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "patrolShuttles");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = shuttlesApi.useAdminSystemShuttles(patrolSystemTypeId!, query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.shuttles.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.shuttles.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateShuttleModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<Shuttle>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="left">{i18n.translation.common.fixed}</th>
            <th align="left">{i18n.translation.systemTypes.plural}</th>
            <th align="left">{i18n.translation.materials.plural}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.item.id}`}>
                {item.item.code}
              </Link>
            </td>
            <td>{item.item.description}</td>
            <td>{formatBoolean(i18n.translation, item.fixed)}</td>
            <td>{item.systems?.map((x) => x.name).join("; ")}</td>
            <td>{item.supportMaterials?.map((x) => x.name).join("; ")}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.shuttles.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                shuttlesApi
                  .deleteAdminShuttle(itemToDelete.id, itemToDelete.systemType.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.shuttles.questions.delete}
        </ModalDialog>
      )}
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.shuttles.edit}>
          <ShuttleForm onClose={() => setItemToEdit(undefined)} defaultValues={itemToEdit} />
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Shuttles;
